import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Button,
    CircularProgress,
    Snackbar,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from "@mui/material";

// Custom hooks
import { useNavigate } from "react-router-dom";
import { withAuth, useAuth } from "@/hooks/useAuth";
import { useTranslation } from "@/hooks/useTranslation";
import { useProductList } from "@/hooks/products/useProductList";

// Components
import LayoutCms from "@/theme/layout-cms";
import Icon from "@/theme/vectors/bundle";
import ProductTable from "@/components/cms/products/ProductTable";
import CreateProductModal from "@/components/cms/products/CreateProductModal";

// Constants
import { PAGE_SIZES, DEFAULT_PAGE_SIZE } from "@/utils/products/constants";

const CmsProductPage = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { t } = useTranslation();
    
    // New product modal state
    const [isModalOpen, setIsModalOpen] = useState(false);
    
    // UI state
    const [notification, setNotification] = useState({ open: false, message: "", severity: "success" });
    
    // Use product list custom hook
    const {
        products,
        loading,
        loadingMore,
        pagination,
        setPagination,
        fetchProducts,
        handleEdit,
        handleDelete,
        handleCreateProduct,
        creatingProduct,
        newProduct,
        setNewProduct,
        handleNewProductChange
    } = useProductList(showNotification, navigate);
    
    // Fetch products when component mounts or pagination changes
    useEffect(() => {
        fetchProducts(pagination.offset, pagination.limit, false);
    }, [pagination.limit]);
    
    // Handle "Load More" click
    const handleLoadMore = () => {
        fetchProducts(pagination.offset, pagination.limit, true);
    };
    
    // Handle page size change
    const handlePageSizeChange = (event) => {
        const newPageSize = parseInt(event.target.value);
        
        // Reset pagination to initial state with new page size
        setPagination({
            offset: 0,
            limit: newPageSize,
            hasMore: true
        });
        
        // Fetch first page with new page size
        fetchProducts(0, newPageSize, false);
    };
    
    // Handle opening the modal for a new product
    const handleAddNew = () => {
        setNewProduct({
            name: "",
            seoSlug: ""
        });
        setIsModalOpen(true);
    };
    
    // Handle creating a new product (from modal)
    const handleCreate = () => {
        handleCreateProduct(newProduct)
            .then(success => {
                if (success) {
                    setIsModalOpen(false);
                }
            });
    };
    
    // Show notification
    function showNotification(message, severity) {
        setNotification({
            open: true,
            message,
            severity
        });
    }
    
    // Close notification
    const handleCloseNotification = () => {
        setNotification({
            ...notification,
            open: false
        });
    };
    
    // Safe translate function to prevent "t is not a function" errors
    const translate = (key, defaultText) => {
        if (typeof t === 'function') {
            return t(key, defaultText);
        }
        return defaultText || key;
    };
    
    return (
        <LayoutCms>
            <Box sx={{ padding: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <Typography variant="h4">{translate('product.title', 'Products')}</Typography>
                </Box>
                
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                    <Button 
                        variant="contained" 
                        startIcon={<Icon name="add" />}
                        onClick={handleAddNew}
                    >
                        {translate('product.addNew', 'Add New')}
                    </Button>
                    
                    <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
                        <InputLabel id="page-size-label">{translate('product.pageSize', 'Page Size')}</InputLabel>
                        <Select
                            labelId="page-size-label"
                            value={pagination.limit}
                            onChange={handlePageSizeChange}
                            label={translate('product.pageSize', 'Page Size')}
                        >
                            {PAGE_SIZES.map(size => (
                                <MenuItem key={size} value={size}>{size}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
                
                {loading && !products.length && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                        <CircularProgress />
                    </Box>
                )}
                
                <ProductTable 
                    products={products}
                    loading={loading}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    translate={translate}
                />
                
                {pagination.hasMore && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 2 }}>
                        <Button 
                            variant="outlined" 
                            onClick={handleLoadMore} 
                            disabled={loadingMore}
                            startIcon={loadingMore && <CircularProgress size={20} />}
                        >
                            {loadingMore ? 
                                translate('common.loading', 'Loading...') : 
                                translate('common.loadMore', 'Load More')}
                        </Button>
                    </Box>
                )}
            </Box>
            
            {/* Create New Product Modal */}
            <CreateProductModal 
                isOpen={isModalOpen}
                onClose={() => !creatingProduct && setIsModalOpen(false)}
                onCreate={handleCreate}
                creating={creatingProduct}
                product={newProduct}
                onChange={handleNewProductChange}
                translate={translate}
            />
            
            {/* Notifications */}
            <Snackbar 
                open={notification.open} 
                autoHideDuration={6000} 
                onClose={handleCloseNotification}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert 
                    onClose={handleCloseNotification} 
                    severity={notification.severity} 
                    sx={{ width: '100%' }}
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </LayoutCms>
    );
};

// Export with auth protection
export default withAuth(CmsProductPage);